import axios from "axios";
import {DevRootURL, EndPoints} from "./EndPoints";
import {
    AddClientDTO,
    AddEditCommitteeDTO,
    AddEditCompanyDTO,
    AddEditCourtDTO,
    AddEditCourtHouseDTO,
    AddEditMailSettingsDTO,
    AddEditNotaryDTO,
    CompanyType,
    CourtType,
    CreateLegalNoticeDTO,
    LegalNoticeStatus
} from "./dtos";
import {getAppToken} from "../shared/utils";
import {AsyncTaskStatusType, ResponseDTO} from "../shared/dtos";
import {
    setAllCitiesLookup,
    setAllClientsLookup,
    setAllCommittees,
    setAllCompanies,
    setAllCourtHouses,
    setAllCourts,
    setAllNotaries,
    setAllPaginatedCities,
    setAllPaginatedClients,
    setAllPaginatedCommittees,
    setAllPaginatedCompanies,
    setAllPaginatedCourtHouses,
    setAllPaginatedCourts,
    setAllPaginatedNotaries,
    setCreateClientTaskStatus,
    setCreateCommitteeTaskStatus,
    setCreateCompanyTaskStatus,
    setCreateCourtTaskStatus,
    setCreateHouseTaskStatus,
    setCreateLegalNoticeTaskStatus,
    setCreateNotaryTaskStatus,
    setDeleteCommitteeTaskStatus,
    setDeleteCompanyTaskStatus,
    setDeleteCourtHouseTaskStatus,
    setDeleteCourtTaskStatus,
    setDeleteNotaryTaskStatus,
    setEditCommitteeTaskStatus,
    setEditCompanyTaskStatus,
    setEditCourtHouseTaskStatus,
    setEditCourtTaskStatus,
    setEditNotaryTaskStatus,
    setEmailSettings,
    setGetAllClientsTaskStatus,
    setGetAllCompaniesTaskStatus,
    setGetAllPaginatedCitiesStatus,
    setGetAllPaginatedClientsTaskStatus,
    setGetAllPaginatedCommitteesTaskStatus,
    setGetAllPaginatedCompaniesTaskStatus,
    setGetAllPaginatedNotariesTaskStatus,
    setLoadAllCommitteesTaskStatus,
    setLoadAllCourtsTaskStatus,
    setLoadAllNotariesTaskStatus,
    setLoadCitiesTaskStatus,
    setLoadCourtHousesTaskStatus,
    setLoadCourtsTaskStatus,
    setLoadEmailSettingsTaskStatus,
    setUpdateEmailSettingsTaskStatus,
} from "../features/settings/settingsSlice";
import {AppState} from "../store";
import {createAsyncThunk} from "@reduxjs/toolkit";
import {
    setAllPaginatedLegalNotices,
    setDeleteNoticeTaskStatus,
    setLoadLegalNoticesTaskStatus,
    setUpdateNoticeStatusTaskStatus
} from "../features/notices/legalNotice";
import moment from "moment";
import {ErrorMessages, Headers} from "./constants";

export const createLegalCaseNoticeAction = createAsyncThunk(
    'legalCase/createLegalCaseNotice',
    async (dto: CreateLegalNoticeDTO, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setCreateLegalNoticeTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.post(`${DevRootURL}/api/legal-notice`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, fieldErrors, errorDetails} = response.data as ResponseDTO;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setCreateLegalNoticeTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setCreateLegalNoticeTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    fieldErrors,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setCreateLegalNoticeTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getLegalNoticesAction = createAsyncThunk(
    'legalCase/getLegalNotices',
    async (
        {
            legalCaseId,
            page = 1,
            textFilter,
            startDateFilter,
            endDateFilter,
            statusFilter,
            userIdFilter
        }: {
            legalCaseId?: number | null;
            page?: number;
            startDateFilter?: Date | null;
            endDateFilter?: Date | null;
            textFilter?: string | null;
            statusFilter?: LegalNoticeStatus | null;
            userIdFilter?: number | null;
        },
        {dispatch, getState, rejectWithValue, fulfillWithValue}
    ) => {
        dispatch(setLoadLegalNoticesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({
                page: page.toString(),
            });
            if (legalCaseId) {
                params.append("legalCaseId", legalCaseId.toString());
            }

            if (textFilter) {
                params.append('nameFilter', textFilter);
            }

            if (startDateFilter) {
                params.append("startDateFilter", moment(startDateFilter).startOf("day").toISOString());
            }

            if (endDateFilter) {
                params.append("endDateFilter", moment(endDateFilter).endOf("day").toISOString());
            }

            if (statusFilter) {
                params.append("statusFilter", statusFilter);
            }

            if (userIdFilter) {
                params.append("userIdFilter", userIdFilter.toString());
            }

            const response = await axios.get(`${EndPoints.legalNotices}/legal-notices-by-filter?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadLegalNoticesTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllPaginatedLegalNotices(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadLegalNoticesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadLegalNoticesTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateLegalNoticeStatusAction = createAsyncThunk(
    'notice/updateLegalNoticeStatus',
    async ({noticeId, newStatus}: { noticeId: number; newStatus: string }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }) => {
        dispatch(setUpdateNoticeStatusTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(`${EndPoints.legalNotices}/${noticeId}/update-status`, {newStatus}, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setUpdateNoticeStatusTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setUpdateNoticeStatusTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            const errorDetails = error.response?.data?.errorDetails || null;
            dispatch(setUpdateNoticeStatusTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue({message: errorMessage, errorDetails});
        }
    }
);

export const deleteLegalNoticeAction = createAsyncThunk(
    'notice/deleteLegalNotice',
    async (noticeId: number, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setDeleteNoticeTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.delete(`${EndPoints.legalNotices}/${noticeId}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });
            const {code, message, errorDetails} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setDeleteNoticeTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setDeleteNoticeTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setDeleteNoticeTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const createCourtHouseAction = createAsyncThunk(
    'settings/createCourtHouse',
    async (dto: AddEditCourtHouseDTO, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setCreateHouseTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.post(EndPoints.courtHouse, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, fieldErrors, errorDetails} = response.data as ResponseDTO;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setCreateHouseTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else if (code === ResponseDTO.CodeEnum.DuplicateObject) {
                const errorMessage = "Bu isimde bir adliye zaten mevcut";
                dispatch(setCreateHouseTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage,
                }));
                return rejectWithValue(errorMessage);
            } else {
                dispatch(setCreateHouseTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    fieldErrors,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setCreateHouseTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const createCourtAction = createAsyncThunk(
    'settings/createCourt',
    async (dto: AddEditCourtDTO, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setCreateCourtTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.post(EndPoints.court, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON
                }
            });

            const {code, message, fieldErrors, errorDetails} = response.data as ResponseDTO;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setCreateCourtTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else if (code === ResponseDTO.CodeEnum.DuplicateObject) {
                dispatch(setCreateCourtTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: ErrorMessages.DUPLICATE_COURT
                }));
                return rejectWithValue(ErrorMessages.DUPLICATE_COURT);
            } else {
                dispatch(setCreateCourtTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    fieldErrors,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setCreateCourtTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllCourtsAction = createAsyncThunk(
    'courts/getAllCourts',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setLoadAllCourtsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }
            const response = await axios.get(EndPoints.courts, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });
            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadAllCourtsTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllCourts(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadAllCourtsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadAllCourtsTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const editCourtAction = createAsyncThunk(
    'courts/editCourt',
    async (
        {id, name, type, courtHouseId}: { id: number; name: string; type: CourtType; courtHouseId: number },
        {dispatch, getState, rejectWithValue, fulfillWithValue}
    ) => {
        dispatch(setEditCourtTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(
                `${EndPoints.court}/${id}`,
                {name, type, courtHouseId},
                {
                    headers: {
                        [Headers.AUTHORIZATION]: `Bearer ${token}`,
                        [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                    },
                }
            );

            const {code, message, errorDetails, resultValue, fieldErrors} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setEditCourtTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(resultValue);
            } else if (code === ResponseDTO.CodeEnum.DuplicateObject) {
                dispatch(setEditCourtTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: ErrorMessages.DUPLICATE_COURT
                }));
                return rejectWithValue(ErrorMessages.DUPLICATE_COURT);
            } else {
                dispatch(setEditCourtTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setEditCourtTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteCourtAction = createAsyncThunk(
    'courts/deleteCourt',
    async (id: number, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setDeleteCourtTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.delete(`${EndPoints.court}/${id}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setDeleteCourtTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setDeleteCourtTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setDeleteCourtTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllCourtHousesAction = createAsyncThunk(
    'courts/getAllCourtHouses',
    async (_, {dispatch, getState, rejectWithValue}) => {
        dispatch(setLoadCourtHousesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }
            const response = await axios.get(EndPoints.courtHouses, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });
            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadCourtHousesTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllCourtHouses(resultValue));
            } else {
                dispatch(setLoadCourtHousesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadCourtHousesTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const editCourtHouseAction = createAsyncThunk(
    'courts/editCourtHouse',
    async (
        {id, name, cityId}: { id: number; name: string; cityId: number },
        {dispatch, getState, rejectWithValue, fulfillWithValue}
    ) => {
        dispatch(setEditCourtHouseTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(
                `${EndPoints.courtHouse}/${id}`,
                {name, cityId},
                {
                    headers: {
                        [Headers.AUTHORIZATION]: `Bearer ${token}`,
                        [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                    },
                }
            );

            const {code, message, errorDetails, resultValue, fieldErrors} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setEditCourtHouseTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setEditCourtHouseTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setEditCourtHouseTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteCourtHouseAction = createAsyncThunk(
    'courts/deleteCourtHouse',
    async (id: number, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setDeleteCourtHouseTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.delete(`${EndPoints.courtHouse}/${id}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setDeleteCourtHouseTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setDeleteCourtHouseTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setDeleteCourtHouseTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const createCompanyAction = createAsyncThunk(
    'settings/createCompany',
    async (dto: AddEditCompanyDTO, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setCreateCompanyTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.post(EndPoints.company, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, fieldErrors} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setCreateCompanyTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else if (code === ResponseDTO.CodeEnum.DuplicateObject) {
                const errorMessage =
                    dto.type === CompanyType.Branch
                        ? "Bu isimde bir şube zaten mevcut"
                        : "Bu isimde bir iştirak zaten mevcut";
                dispatch(setCreateCompanyTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage,
                }));
                return rejectWithValue(errorMessage);
            } else {
                dispatch(setCreateCompanyTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                    fieldErrors,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setCreateCompanyTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const editCompanyAction = createAsyncThunk(
    'settings/editCompany',
    async ({id, dto}: { id: number; dto: AddEditCompanyDTO }, {dispatch, getState, rejectWithValue}) => {
        dispatch(setEditCompanyTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            await axios.put(`${EndPoints.company}/${id}`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            dispatch(setEditCompanyTaskStatus({type: AsyncTaskStatusType.Success}));
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setEditCompanyTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteCompanyAction = createAsyncThunk(
    'settings/deleteCompany',
    async (id: number, {dispatch, getState, rejectWithValue}) => {
        dispatch(setDeleteCompanyTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            await axios.delete(`${EndPoints.company}/${id}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            dispatch(setDeleteCompanyTaskStatus({type: AsyncTaskStatusType.Success}));
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setDeleteCompanyTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllCompaniesAction = createAsyncThunk(
    'settings/getAllCompanies',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setGetAllCompaniesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.get(EndPoints.companies, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setAllCompanies(resultValue));
                dispatch(setGetAllCompaniesTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetAllCompaniesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetAllCompaniesTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllCompaniesByNameAction = createAsyncThunk(
    'settings/getAllCompaniesByName',
    async ({typeFilter, page = 1}: { typeFilter: CompanyType, page?: number }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }) => {
        dispatch(setGetAllPaginatedCompaniesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({filterByName: "", page: page.toString()});
            if (typeFilter) params.append('typeFilter', typeFilter);

            const response = await axios.get(`${EndPoints.settings}/companies-by-name?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setAllPaginatedCompanies(resultValue));
                dispatch(setGetAllPaginatedCompaniesTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetAllPaginatedCompaniesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetAllPaginatedCompaniesTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const createClientAction = createAsyncThunk(
    'settings/createClient',
    async (dto: AddClientDTO, {dispatch, getState, fulfillWithValue, rejectWithValue}) => {
        dispatch(setCreateClientTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.post(EndPoints.client, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, fieldErrors} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setCreateClientTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else if (code === ResponseDTO.CodeEnum.DuplicateObject) {
                dispatch(setCreateClientTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: 'Bu isimde karşı taraf var'
                }));
                return rejectWithValue("Bu isimde karşı taraf var");
            } else {
                dispatch(setCreateClientTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setCreateClientTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);
/*
export const editClientAction = createAsyncThunk(
    'settings/editClient',
    async ({id, dto}: { id: number, dto: AddEditClientDTO }, {dispatch, getState, rejectWithValue}) => {
        dispatch(setEditClientTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            await axios.put(`${EndPoints.client}/${id}`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            dispatch(setEditClientTaskStatus({type: AsyncTaskStatusType.Success}));
            return true;
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setEditClientTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteClientAction = createAsyncThunk(
    'settings/deleteClient',
    async (id: number, {dispatch, getState, rejectWithValue}) => {
        dispatch(setDeleteClientTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            await axios.delete(`${EndPoints.client}/${id}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            dispatch(setDeleteClientTaskStatus({type: AsyncTaskStatusType.Success}));
            return true;
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setDeleteClientTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);
*/

export const getAllClientsAction = createAsyncThunk(
    'settings/getAllClients',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setGetAllClientsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.get(EndPoints.clients, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetAllClientsTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllClientsLookup(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetAllClientsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetAllClientsTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllPaginatedClientsAction = createAsyncThunk(
    'settings/getAllPaginatedClients',
    async ({filterByName, page = 1}: { filterByName?: string; page: number }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }) => {
        dispatch(setGetAllPaginatedClientsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({page: page.toString()});
            if (filterByName) params.append('filterByName', filterByName);

            const response = await axios.get(`${EndPoints.settings}/clients-by-name?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetAllPaginatedClientsTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllPaginatedClients(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetAllPaginatedClientsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetAllPaginatedClientsTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getLoadCitiesAction = createAsyncThunk(
    'settings/getLoadCities',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setLoadCitiesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.get(EndPoints.cities, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadCitiesTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllCitiesLookup(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadCitiesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadCitiesTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllPaginatedCitiesAction = createAsyncThunk(
    'settings/getAllPaginatedCities',
    async ({page, filterByName}: { page: number; filterByName: string }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }) => {
        dispatch(setGetAllPaginatedCitiesStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({page: page.toString(), filterByName});

            const response = await axios.get(`${EndPoints.settings}/cities-by-name?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetAllPaginatedCitiesStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllPaginatedCities(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetAllPaginatedCitiesStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetAllPaginatedCitiesStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getCourtHousesAction = createAsyncThunk(
    'settings/getCourtHouses',
    async ({filterByName, cityPlateId, page = 1}: {
        filterByName?: string, cityPlateId?: number, page: number
    }, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setLoadCourtHousesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({
                filterByName: '',
                page: page.toString()
            });
            if (filterByName) params.append('filterByName', filterByName);
            if (cityPlateId != null) params.append('cityPlateId', cityPlateId.toString());

            const response = await axios.get(`${EndPoints.settings}/court-houses-by-filter?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadCourtHousesTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllPaginatedCourtHouses(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadCourtHousesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadCourtHousesTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getCourtsAction = createAsyncThunk(
    'settings/getCourts',
    async ({courtHouseId, page = 1, filterByTypes}: {
        courtHouseId: number,
        page: number,
        filterByTypes?: CourtType[]
    }, {
               dispatch,
               getState,
               rejectWithValue,
               fulfillWithValue
           }) => {
        dispatch(setLoadCourtsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({
                page: page.toString(),
                courtHouseId: courtHouseId.toString(),
            });

            if (filterByTypes && filterByTypes.length > 0) {
                params.append('filterByTypes', filterByTypes.join(','));
            }

            const response = await axios.get(`${EndPoints.settings}/courts-by-filter?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadCourtsTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllPaginatedCourts(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadCourtsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadCourtsTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getLoadEmailSettingsAction = createAsyncThunk(
    'settings/getLoadEmailSettings',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setLoadEmailSettingsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.get(`${EndPoints.settings}/email`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadEmailSettingsTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setEmailSettings(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadEmailSettingsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadEmailSettingsTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateEmailSettingsAction = createAsyncThunk(
    'settings/updateEmailSettings',
    async (newSettings: AddEditMailSettingsDTO, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setUpdateEmailSettingsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(`${EndPoints.settings}/email`, newSettings, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, fieldErrors} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setUpdateEmailSettingsTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(newSettings);
            } else {
                dispatch(setUpdateEmailSettingsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setUpdateEmailSettingsTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const createNotaryAction = createAsyncThunk(
    'settings/createNotary',
    async (dto: AddEditNotaryDTO, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setCreateNotaryTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.post(`${EndPoints.settings}/notary`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, fieldErrors, errorDetails} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setCreateNotaryTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else if (code === ResponseDTO.CodeEnum.DuplicateObject) {
                const errorMessage = "Bu isimde bir noter zaten mevcut";
                dispatch(setCreateNotaryTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage,
                }));
                return rejectWithValue(errorMessage);
            } else {
                dispatch(setCreateNotaryTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    fieldErrors,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setCreateNotaryTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const editNotaryAction = createAsyncThunk(
    'settings/editNotary',
    async ({id, dto}: { id: number; dto: AddEditNotaryDTO }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }) => {
        dispatch(setEditNotaryTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(`${EndPoints.settings}/notary/${id}`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, fieldErrors} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setEditNotaryTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setEditNotaryTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setEditNotaryTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteNotaryAction = createAsyncThunk(
    'settings/deleteNotary',
    async (id: number, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setDeleteNotaryTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.delete(`${EndPoints.settings}/notary/${id}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setDeleteNotaryTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setDeleteNotaryTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setDeleteNotaryTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const createCommitteeAction = createAsyncThunk(
    'settings/createCommittee',
    async (dto: AddEditCommitteeDTO, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setCreateCommitteeTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.post(`${EndPoints.settings}/committee`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, fieldErrors, errorDetails} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setCreateCommitteeTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else if (code === ResponseDTO.CodeEnum.DuplicateObject) {
                const errorMessage = "Bu isimde bir Tüketici Hakem Heyeti mevcut";
                dispatch(setCreateCommitteeTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage,
                }));
                return rejectWithValue(errorMessage);
            } else {
                dispatch(setCreateCommitteeTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    fieldErrors,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setCreateCommitteeTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const editCommitteeAction = createAsyncThunk(
    'settings/editCommittee',
    async ({id, dto}: { id: number; dto: AddEditCommitteeDTO }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }) => {
        dispatch(setEditCommitteeTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(`${EndPoints.settings}/committee/${id}`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, fieldErrors} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setEditCommitteeTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setEditCommitteeTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setEditCommitteeTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteCommitteeAction = createAsyncThunk(
    'settings/deleteCommittee',
    async (id: number, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setDeleteCommitteeTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.delete(`${EndPoints.settings}/committee/${id}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setDeleteCommitteeTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setDeleteCommitteeTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setDeleteCommitteeTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllNotariesAction = createAsyncThunk(
    'settings/getAllNotaries',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setLoadAllNotariesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }
            const response = await axios.get(`${EndPoints.settings}/notaries/`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });
            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadAllNotariesTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllNotaries(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadAllNotariesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadAllNotariesTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllPaginatedNotariesAction = createAsyncThunk(
    'settings/getAllPaginatedNotaries',
    async ({filterByName, page = 1}: { filterByName?: string; page: number }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }) => {
        dispatch(setGetAllPaginatedNotariesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({page: page.toString()});
            if (filterByName) params.append('filterByName', filterByName);

            const response = await axios.get(`${EndPoints.settings}/notaries-by-filter?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetAllPaginatedNotariesTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllPaginatedNotaries(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetAllPaginatedNotariesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetAllPaginatedNotariesTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllCommitteesAction = createAsyncThunk(
    'settings/getAllCommittees',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setLoadAllCommitteesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error("Token is missing");
            }

            const response = await axios.get(`${EndPoints.settings}/committees`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadAllCommitteesTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllCommittees(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadAllCommitteesTaskStatus({type: AsyncTaskStatusType.Error, errorMessage: message}));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || "Unknown error";
            dispatch(setLoadAllCommitteesTaskStatus({type: AsyncTaskStatusType.Error, errorMessage}));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllPaginatedCommitteesAction = createAsyncThunk(
    'settings/getAllPaginatedCommittees',
    async ({filterByName, page = 1}: { filterByName?: string; page: number }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }) => {
        dispatch(setGetAllPaginatedCommitteesTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error("Token is missing");
            }

            const params = new URLSearchParams({page: page.toString()});
            if (filterByName) params.append('filterByName', filterByName);

            const response = await axios.get(`${EndPoints.settings}/committees-by-filter?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetAllPaginatedCommitteesTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllPaginatedCommittees(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetAllPaginatedCommitteesTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || "Unknown error";
            dispatch(setGetAllPaginatedCommitteesTaskStatus({type: AsyncTaskStatusType.Error, errorMessage}));
            return rejectWithValue(errorMessage);
        }
    }
);